"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calculator, ChevronDown, DollarSign, InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Cell,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import * as z from "zod";

const basicFormSchema = z.object({
  annualIncome: z.number().min(0),
  monthlyDebts: z.number().min(0),
  downPayment: z.number().min(0),
});

const advancedFormSchema = z.object({
  debtToIncome: z.number().min(0).max(100),
  interestRate: z.number().min(0).max(100),
  loanTerm: z.number().min(0),
  includePMI: z.boolean(),
  includeTaxesInsurance: z.boolean(),
  propertyTax: z.number().min(0).max(100),
  homeInsurance: z.number().min(0),
  hoaDues: z.number().min(0),
});

const tooltips = {
  annualIncome:
    "Your total yearly income before taxes, including salary, bonuses, and other sources of income",
  monthlyDebts:
    "Monthly payments for credit cards, car loans, student loans, and other debt obligations",
  downPayment: "The amount you plan to pay upfront when purchasing the home",
  debtToIncome:
    "The percentage of your monthly income that goes toward paying debts. Lower ratios are better for qualifying",
  interestRate:
    "The annual interest rate for your mortgage. This affects your monthly payments",
};

const disclaimer = {
  title: "Calculator Assumptions & Disclaimers",
  description: `This calculator provides estimates based on the following assumptions:

• Monthly housing payment limited to 28% of gross monthly income
• Total monthly debt payments (including housing) limited to 36% of gross monthly income
• 30-year fixed-rate mortgage
• Property tax rate of 1.2% annually
• Annual homeowners insurance of $1,000
• Private Mortgage Insurance (PMI) if down payment is less than 20%
• Does not include HOA fees, utilities, or maintenance costs

This calculator is for educational purposes only. Your actual home affordability may vary based on factors including:
• Your credit score and history
• Specific lender requirements
• Local market conditions
• Property type and location
• Other financial obligations

Please consult with a qualified mortgage lender for a detailed assessment of your home buying power.`,
};

const formatNumber = (value: number): string => {
  return value.toLocaleString("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

function InputWithTooltip({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <FormItem>
      <div className="flex items-center gap-2">
        <FormLabel>{label}</FormLabel>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <FormControl>{children}</FormControl>
    </FormItem>
  );
}

const COLORS = {
  "P & I": "#211F20", // smokeyBlack
  PMI: "#E6C1A9", // plaster-light
  Taxes: "#CF9D7C", // plaster
  Insurance: "#9D6A4E", // plaster-dark
};

export const HomeAffordabilityCalculator = () => {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [maxPrice, setMaxPrice] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(1837);

  const basicForm = useForm<z.infer<typeof basicFormSchema>>({
    resolver: zodResolver(basicFormSchema),
    defaultValues: {
      annualIncome: 70000,
      monthlyDebts: 250,
      downPayment: 20000,
    },
  });

  const advancedForm = useForm<z.infer<typeof advancedFormSchema>>({
    resolver: zodResolver(advancedFormSchema),
    defaultValues: {
      debtToIncome: 36,
      interestRate: 7,
      loanTerm: 360,
      includePMI: true,
      includeTaxesInsurance: true,
      propertyTax: 1.2,
      homeInsurance: 945,
      hoaDues: 0,
    },
    mode: "onChange",
  });

  // Watch all form values
  const formValues = basicForm.watch();
  const advancedValues = advancedForm.watch();

  const calculateMonthlyPayment = (price: number, downPayment: number) => {
    const loanAmount = price - downPayment;
    const interestRate = advancedValues.interestRate / 100;
    const monthlyRate = interestRate / 12;
    const loanTerm = advancedValues.loanTerm;

    const principalAndInterest =
      (loanAmount * (monthlyRate * (1 + monthlyRate) ** loanTerm)) /
      ((1 + monthlyRate) ** loanTerm - 1);

    // Calculate PMI (if down payment < 20% and includePMI is true)
    const pmi =
      advancedValues.includePMI && downPayment / price < 0.2
        ? (loanAmount * 0.01) / 12
        : 0;

    // Property tax and insurance if included
    const monthlyTax = advancedValues.includeTaxesInsurance
      ? (price * (advancedValues.propertyTax / 100)) / 12
      : 0;

    const monthlyInsurance = advancedValues.includeTaxesInsurance
      ? advancedValues.homeInsurance / 12
      : 0;

    // Add HOA dues
    const hoaDues = advancedValues.hoaDues || 0;

    return {
      total: Math.round(
        principalAndInterest + pmi + monthlyTax + monthlyInsurance + hoaDues,
      ),
      breakdown: {
        "P & I": Math.round(principalAndInterest),
        PMI: Math.round(pmi),
        Taxes: Math.round(monthlyTax),
        Insurance: Math.round(monthlyInsurance),
        ...(hoaDues > 0 ? { HOA: Math.round(hoaDues) } : {}),
      },
    };
  };

  const [breakdown, setBreakdown] = useState<Record<string, number>>({
    "P & I": 1396,
    PMI: 133,
    Taxes: 230,
    Insurance: 79,
  });

  // Update calculations when any relevant value changes
  useEffect(() => {
    const newMaxPrice = calculateMaxPrice(formValues);
    setMaxPrice(newMaxPrice);

    const newPayments = calculateMonthlyPayment(
      newMaxPrice,
      formValues.downPayment,
    );
    setMonthlyPayment(newPayments.total);
    setBreakdown(newPayments.breakdown);
  }, [
    // Basic form values
    formValues.annualIncome,
    formValues.monthlyDebts,
    formValues.downPayment,
    // Advanced form values
    advancedValues.debtToIncome,
    advancedValues.interestRate,
    advancedValues.loanTerm,
    advancedValues.includePMI,
    advancedValues.includeTaxesInsurance,
    advancedValues.propertyTax,
    advancedValues.homeInsurance,
    advancedValues.hoaDues,
  ]);

  const handleSliderChange = (value: number[]) => {
    const newMonthlyPayment = value[0];
    setMonthlyPayment(newMonthlyPayment);

    // Calculate new max price based on monthly payment
    const interestRate = advancedForm.getValues().interestRate / 100;
    const monthlyRate = interestRate / 12;
    const loanTerm = advancedForm.getValues().loanTerm;
    const downPayment = basicForm.getValues().downPayment;

    // Reverse calculate house price from monthly payment
    const paymentWithoutTaxInsurance =
      newMonthlyPayment - 1000 / 12 - (maxPrice * 0.012) / 12;
    const loanAmount =
      (paymentWithoutTaxInsurance * ((1 + monthlyRate) ** loanTerm - 1)) /
      (monthlyRate * (1 + monthlyRate) ** loanTerm);

    const newPrice = Math.round(loanAmount + downPayment);
    setMaxPrice(newPrice);

    const newPayments = calculateMonthlyPayment(newPrice, downPayment);
    setBreakdown(newPayments.breakdown);
  };

  const calculateMaxPrice = (values: z.infer<typeof basicFormSchema>) => {
    const monthlyIncome = values.annualIncome / 12;
    const debtToIncomeRatio = advancedValues.debtToIncome / 100;
    const maxMonthlyPayment = Math.max(
      0,
      monthlyIncome * debtToIncomeRatio - values.monthlyDebts,
    );

    const interestRate = advancedValues.interestRate / 100;
    const monthlyRate = interestRate / 12;
    const loanTerm = advancedValues.loanTerm;
    const downPayment = values.downPayment;

    // Account for taxes, insurance, and HOA in max payment
    const monthlyTaxRate = advancedValues.includeTaxesInsurance
      ? advancedValues.propertyTax / 100 / 12
      : 0;
    const monthlyInsurance = advancedValues.includeTaxesInsurance
      ? advancedValues.homeInsurance / 12
      : 0;
    const hoaDues = advancedValues.hoaDues || 0;

    // Adjust max payment for additional costs
    const adjustedMaxPayment = maxMonthlyPayment - monthlyInsurance - hoaDues;

    // Calculate max loan amount considering PMI
    const pmiRate =
      advancedValues.includePMI && downPayment / maxPrice < 0.2 ? 0.01 / 12 : 0;

    // Use financial formula to solve for max price
    const maxLoanAmount =
      (adjustedMaxPayment * (1 - monthlyTaxRate)) /
      (monthlyRate +
        pmiRate +
        monthlyRate / ((1 + monthlyRate) ** loanTerm - 1));

    return Math.max(0, Math.round(maxLoanAmount + downPayment));
  };

  const calculateHousePriceFromPayment = (monthlyPayment: number) => {
    const interestRate = advancedForm.getValues().interestRate / 100;
    const monthlyRate = interestRate / 12;
    const loanTerm = advancedForm.getValues().loanTerm;
    const downPayment = basicForm.getValues().downPayment;

    const loanAmount =
      monthlyPayment * ((1 - (1 + monthlyRate) ** -loanTerm) / monthlyRate);
    return Math.max(0, Math.round(loanAmount + downPayment));
  };

  const result = {
    total: 1837,
    maxPrice: calculateMaxPrice(basicForm.getValues()),
    breakdown: {
      "P & I": 1396,
      PMI: 133,
      Insurance: 79,
      Taxes: 230,
    },
  };

  return (
    <Card className="w-full overflow-hidden">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between border-b p-4 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-blue-50 p-2">
            <Calculator className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold">Home Affordability Calculator</h3>
            <p className="text-sm text-gray-600">
              Estimate how much house you can afford based on your income,
              debts, and savings
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setIsAdvanced(!isAdvanced)}
          className="shrink-0"
        >
          {isAdvanced ? "Simple Mode" : "Advanced Mode"}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="p-6">
        <p className="text-sm text-gray-500 mb-6">
          Enter your financial details to see the maximum home price you can
          afford based on your income, debts, and down payment.
        </p>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-6">
            <Form {...basicForm}>
              <form className="space-y-6">
                <FormField
                  control={basicForm.control}
                  name="annualIncome"
                  render={({ field }) => (
                    <InputWithTooltip
                      label="Annual Income"
                      tooltip={tooltips.annualIncome}
                    >
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                        <Input
                          {...field}
                          className="pl-9"
                          value={formatNumber(field.value)}
                          onChange={(e) => {
                            const value =
                              Number.parseInt(
                                e.target.value.replace(/[^\d]/g, ""),
                              ) || 0;
                            field.onChange(value);
                          }}
                        />
                      </div>
                    </InputWithTooltip>
                  )}
                />

                <FormField
                  control={basicForm.control}
                  name="monthlyDebts"
                  render={({ field }) => (
                    <InputWithTooltip
                      label="Monthly Debts"
                      tooltip={tooltips.monthlyDebts}
                    >
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                        <Input
                          {...field}
                          className="pl-9"
                          value={formatNumber(field.value)}
                          onChange={(e) => {
                            const value =
                              Number.parseInt(
                                e.target.value.replace(/[^\d]/g, ""),
                              ) || 0;
                            field.onChange(value);
                          }}
                        />
                      </div>
                    </InputWithTooltip>
                  )}
                />

                <FormField
                  control={basicForm.control}
                  name="downPayment"
                  render={({ field }) => (
                    <InputWithTooltip
                      label="Down Payment"
                      tooltip={tooltips.downPayment}
                    >
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                        <Input
                          {...field}
                          className="pl-9"
                          value={formatNumber(field.value)}
                          onChange={(e) => {
                            const value =
                              Number.parseInt(
                                e.target.value.replace(/[^\d]/g, ""),
                              ) || 0;
                            field.onChange(value);
                          }}
                        />
                      </div>
                    </InputWithTooltip>
                  )}
                />
              </form>
            </Form>

            {isAdvanced && (
              <Form {...advancedForm}>
                <div className="space-y-6 pt-6 border-t">
                  <FormField
                    control={advancedForm.control}
                    name="debtToIncome"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Debt-to-Income Ratio (%)"
                        tooltip={tooltips.debtToIncome}
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="100"
                          onChange={(e) => {
                            const value = Number.parseFloat(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                  <FormField
                    control={advancedForm.control}
                    name="interestRate"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Interest Rate (%)"
                        tooltip={tooltips.interestRate}
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          onChange={(e) => {
                            const value = Number.parseFloat(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                  <FormField
                    control={advancedForm.control}
                    name="loanTerm"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Loan Term (months)"
                        tooltip="Length of the mortgage loan in months (e.g., 360 for 30 years)"
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          onChange={(e) => {
                            const value = Number.parseInt(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                  <FormField
                    control={advancedForm.control}
                    name="propertyTax"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Property Tax Rate (%)"
                        tooltip="Annual property tax rate as a percentage of home value"
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          onChange={(e) => {
                            const value = Number.parseFloat(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                  <FormField
                    control={advancedForm.control}
                    name="homeInsurance"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Annual Home Insurance ($)"
                        tooltip="Estimated annual cost of home insurance"
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          onChange={(e) => {
                            const value = Number.parseInt(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                  <FormField
                    control={advancedForm.control}
                    name="hoaDues"
                    render={({ field }) => (
                      <InputWithTooltip
                        label="Monthly HOA Dues ($)"
                        tooltip="Monthly homeowners association fees, if applicable"
                      >
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          onChange={(e) => {
                            const value = Number.parseInt(e.target.value);
                            field.onChange(Number.isNaN(value) ? 0 : value);
                          }}
                        />
                      </InputWithTooltip>
                    )}
                  />
                </div>
              </Form>
            )}
          </div>

          <ResultDisplay
            maxPrice={maxPrice}
            monthlyPayment={monthlyPayment}
            breakdown={breakdown}
            onSliderChange={handleSliderChange}
          />
        </div>
      </div>

      <div className="p-4 text-center text-sm text-gray-500 border-t">
        <button
          onClick={() => setShowDisclaimer(true)}
          className="text-blue-600 hover:underline"
          type="button"
        >
          View calculator assumptions and disclaimers
        </button>
      </div>

      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{disclaimer.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-line">
              {disclaimer.description}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

interface ResultDisplayProps {
  maxPrice: number;
  monthlyPayment: number;
  breakdown: Record<string, number>;
  onSliderChange: (value: number[]) => void;
}

function ResultDisplay({
  maxPrice,
  monthlyPayment,
  breakdown,
  onSliderChange,
}: ResultDisplayProps) {
  const pieData = Object.entries(breakdown).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-semibold">You can afford a house up to</h3>
        <div className="text-4xl font-bold text-canary mt-2">
          ${maxPrice.toLocaleString()}
        </div>
        <p className="text-muted-foreground mt-2">
          Based on your income, a house at this price should fit comfortably
          within your budget.
        </p>
      </div>

      <div className="flex flex-col items-center">
        {/* Chart Container */}
        <div className="relative w-full max-w-[300px] aspect-square">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius="70%"
                outerRadius="100%"
                paddingAngle={2}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry}`}
                    fill={COLORS[entry.name as keyof typeof COLORS]}
                    strokeWidth={0}
                  />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value: number) => `$${value.toLocaleString()}`}
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "0.5rem",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">
                Monthly Payment
              </div>
              <div className="text-3xl font-bold text-canary">
                ${monthlyPayment.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 w-full max-w-[300px] space-y-2">
          {Object.entries(breakdown).map(([label, amount]) => (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="h-2 w-2 rounded-full"
                  style={{
                    backgroundColor: COLORS[label as keyof typeof COLORS],
                  }}
                />
                <span className="text-sm">{label}</span>
              </div>
              <span className="text-sm font-semibold">
                ${amount.toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        {/* Slider */}
        <div className="mt-8 w-full max-w-[300px]">
          <div className="text-sm text-center text-muted-foreground">
            Adjust Monthly Payment
          </div>
          <Slider
            value={[monthlyPayment]}
            min={1000}
            max={10000}
            step={100}
            onValueChange={onSliderChange}
            className="py-6"
          />
        </div>
      </div>
    </div>
  );
}
